"use client";

import { classNamesExtended } from "@/helpers/common/classNames";
import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";

interface VPInputModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (vpAmount: number) => void;
    title: string;
    isLoading?: boolean;
}

const VPInputModal: React.FC<VPInputModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    isLoading = false,
}) => {
    const [vpAmount, setVpAmount] = useState<string>("");
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (isOpen) {
            setVpAmount("");
            setError("");
        }
    }, [isOpen]);

    const handleConfirm = () => {
        const amount = parseInt(vpAmount, 10);

        if (!vpAmount || isNaN(amount)) {
            setError("Please enter a valid VP amount.");
            return;
        }

        if (amount <= 0) {
            setError("VP amount must be greater than 0.");
            return;
        }

        if (amount > 10000) {
            setError("VP amount must be 10,000 or less.");
            return;
        }

        onConfirm(amount);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !isLoading) {
            handleConfirm();
        }
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-1000" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="bg-brand_charcoal/80 fixed inset-0 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="bg-brand_charcoal border-brand relative z-1000 w-full transform overflow-hidden rounded-lg border-4 p-6 text-left shadow-xl transition-all sm:max-w-md">
                                <Dialog.Title className="text-brand mb-4 text-center text-2xl font-bold">
                                    {title}
                                </Dialog.Title>

                                <div className="mb-4">
                                    <label
                                        htmlFor="vpAmount"
                                        className="text-brand mb-2 block text-center font-semibold"
                                    >
                                        VP Amount:
                                    </label>
                                    <input
                                        id="vpAmount"
                                        type="number"
                                        min="1"
                                        max="10000"
                                        value={vpAmount}
                                        onChange={(e) => {
                                            setVpAmount(e.target.value);
                                            setError("");
                                        }}
                                        onKeyPress={handleKeyPress}
                                        disabled={isLoading}
                                        className={classNamesExtended(
                                            "bg-brand_charcoal text-brand w-full rounded border-2 px-4 py-2 text-center text-lg",
                                            "focus:border-brand focus:outline-none",
                                            {
                                                "border-brand": !error,
                                                "border-red-500": !!error,
                                                "cursor-not-allowed opacity-50":
                                                    isLoading,
                                            },
                                        )}
                                        placeholder="Enter VP amount (1-10000)"
                                        autoFocus
                                    />
                                    {error && (
                                        <p className="mt-2 text-center text-sm text-red-500">
                                            {error}
                                        </p>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        onClick={onClose}
                                        disabled={isLoading}
                                        className={classNamesExtended(
                                            "text-brand border-brand rounded border-2 px-4 py-2 transition-colors",
                                            "bg-brand_charcoal hover:bg-brand hover:text-brand_charcoal",
                                            {
                                                "cursor-not-allowed opacity-50":
                                                    isLoading,
                                            },
                                        )}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        disabled={isLoading}
                                        className={classNamesExtended(
                                            "text-brand_charcoal border-brand rounded border-2 px-4 py-2 transition-colors",
                                            "bg-brand hover:bg-brand_charcoal hover:text-brand",
                                            {
                                                "cursor-not-allowed opacity-50":
                                                    isLoading,
                                            },
                                        )}
                                    >
                                        {isLoading
                                            ? "Processing..."
                                            : "Confirm"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default VPInputModal;
